import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import {
  addDoc, updateDoc, doc, collection,
} from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { VendorConfig, VendorConfigReq } from '../types/VendorConfigTypes';
import { useUser } from '../../api/user-api/UserAPI';

interface ProcessedVendor {
  label: string;
  value: string;
  vendorId: string;
}

export interface CostCategory {
  id: string;
  label: string;
}

export type { ProcessedVendor };

interface VendorConfigModalProps {
  open: boolean;
  onClose: () => void;
  editingConfig: VendorConfig | null;
  vendors: ProcessedVendor[];
  selectedVendor: string;
  selectedChargeTypes: string[];
  onVendorChange: (vendor: string) => void;
  onChargeTypesChange: (chargeTypes: string[]) => void;
  onSave: () => void;
  costCategories: CostCategory[];
  loadingCategories: boolean;
  existingConfigs: VendorConfig[];
}

function VendorConfigModal({
  open,
  onClose,
  editingConfig,
  vendors,
  selectedVendor,
  selectedChargeTypes,
  onVendorChange,
  onChargeTypesChange,
  costCategories,
  loadingCategories,
  existingConfigs,
}: VendorConfigModalProps) {
  const user = useUser();
  // Filter out vendors that already have configurations, except when editing
  const availableVendors = editingConfig
    ? vendors // When editing, show all vendors
    : vendors.filter((vendor) => !existingConfigs.some((config) => config.vendorId === vendor.vendorId));

  const handleChargeTypeChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const newChargeTypes = typeof value === 'string' ? value.split(',') : value;
    onChargeTypesChange(newChargeTypes);
  };

  const handleSave = async () => {
    if (!selectedVendor || selectedChargeTypes.length === 0) {
      enqueueSnackbar('Please select a vendor and at least one utility charge type', { variant: 'error' });
      return;
    }

    const selectedVendorData = vendors.find((v) => v.vendorId === selectedVendor);
    if (!selectedVendorData) {
      enqueueSnackbar('Selected vendor not found', { variant: 'error' });
      return;
    }

    const configData: VendorConfigReq = {
      vendorId: selectedVendorData.vendorId,
      vendorName: selectedVendorData.label,
      allowedChargeTypes: selectedChargeTypes,
      updatedAt: new Date(),
      lastUpdatedBy: {
        email: user?.email || '',
        name: user?.displayName || '',
      },
    };

    try {
      if (editingConfig?.id) {
        await updateDoc(doc(db, 'vendorConfigs', editingConfig.id), configData);
        enqueueSnackbar('Vendor configuration updated successfully', { variant: 'success' });
      } else {
        configData.createdAt = new Date();
        await addDoc(collection(db, 'vendorConfigs'), configData);
        enqueueSnackbar('Vendor configuration created successfully', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      console.error('Error saving vendor config:', error);
      enqueueSnackbar('Error saving vendor configuration', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingConfig ? 'Edit Vendor Configuration' : 'Add Vendor Configuration'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Autocomplete
            options={availableVendors}
            getOptionLabel={(option) => option.label}
            value={vendors.find((v) => v.vendorId === selectedVendor) || null}
            onChange={(event, newValue) => {
              onVendorChange(newValue ? newValue.vendorId : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                fullWidth
              />
            )}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <InputLabel>Allowed Utility Charge Types</InputLabel>
            <Select
              multiple
              value={selectedChargeTypes}
              onChange={handleChargeTypeChange}
              input={<OutlinedInput label="Allowed Utility Charge Types" />}
              disabled={loadingCategories}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const category = costCategories.find((cat) => cat.id === value);
                    return (
                      <Chip key={value} label={category?.label || value} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {loadingCategories ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                costCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedVendor || selectedChargeTypes.length === 0}
          sx={{
            backgroundColor: 'var(--westland-red)',
            '&:hover': {
              backgroundColor: 'var(--westland-red-darker)',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666',
            },
          }}
        >
          {editingConfig ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VendorConfigModal;
